import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/composites/PageHeader';
import { DataTable } from '@/components/composites/DataTable';
import { Select } from '@/components/primitives/Select';
import { TextInput } from '@/components/primitives/TextInput';
import { useAppContext } from '@/context/AppContext';
import { useAppTitleLog } from '@/hooks/useAppTitleLog';
import { useClients } from '@/hooks/useClients';
import { useProjects } from '@/hooks/useProjects';
import { useEmployees } from '@/hooks/useEmployees';
import { formatDayShort } from '@/lib/dates';
import type { AppTitleLogRow } from '@/types/reports';
import styles from './AppTitleLogPage.module.css';

type ColumnKey =
  | 'employee'
  | 'app'
  | 'title'
  | 'host'
  | 'client'
  | 'project'
  | 'start'
  | 'end'
  | 'sessions'
  | 'events'
  | 'time'
  | 'review';

const ROW_KEY_SEP = '\x1f';

/**
 * Activity log report page — task 169.
 *
 * Groups classified events by six dimensions: employee, app, title, host,
 * client, project. Each unique six-tuple is its own row. Backend filters
 * narrow by employee / client / project (ID-based). Client-side text
 * filters narrow by app / title / host after the data loads. All columns
 * are sortable; the default sort is time descending.
 */
export function AppTitleLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mockupMode, dateRange } = useAppContext();

  // Period comes from the global top-bar DateRangePicker via AppContext —
  // mirrors GroupedReportPage, ApplicationsReportPage, etc.
  const periodQs = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return null;
    const params = new URLSearchParams();
    params.set('period', 'custom');
    params.set('startDate', dateRange.start);
    params.set('endDate', dateRange.end);
    return params.toString();
  }, [dateRange.start, dateRange.end]);

  // Backend filters — stored in URL, trigger re-fetch when changed.
  const clientId = searchParams.get('clientId') ?? '';
  const projectId = searchParams.get('projectId') ?? '';
  const employeeId = searchParams.get('employeeId') ?? '';

  // Client-side text filters — stored in URL for shareability but applied
  // locally against the loaded rows, no re-fetch on change.
  const appFilter = searchParams.get('filterApp') ?? '';
  const titleFilter = searchParams.get('filterTitle') ?? '';
  const hostFilter = searchParams.get('filterHost') ?? '';

  const { data: clients } = useClients();
  const { data: projects } = useProjects();
  const { data: employees } = useEmployees();

  const { data: report, isLoading } = useAppTitleLog({
    periodQs: mockupMode ? null : periodQs,
    clientId: clientId || undefined,
    projectId: projectId || undefined,
    employeeId: employeeId || undefined,
  });

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const onPickClient = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (!val) {
      next.delete('clientId');
      next.delete('projectId');
    } else {
      next.set('clientId', val);
      if (projectId) {
        const proj = projects.find((p) => p.id === projectId);
        if (proj && proj.clientId !== val) next.delete('projectId');
      }
    }
    setSearchParams(next);
  };

  const filteredProjects = useMemo(
    () => (clientId ? projects.filter((p) => p.clientId === clientId) : projects),
    [projects, clientId],
  );

  // Client-side filtering applied after the backend data loads.
  const filteredRows = useMemo(() => {
    let rows = report?.rows ?? [];
    const af = appFilter.toLowerCase();
    const tf = titleFilter.toLowerCase();
    const hf = hostFilter.toLowerCase();
    if (af) rows = rows.filter((r) => r.app.toLowerCase().includes(af));
    if (tf) rows = rows.filter((r) => r.title.toLowerCase().includes(tf));
    if (hf) rows = rows.filter((r) => r.host.toLowerCase().includes(hf));
    return rows;
  }, [report?.rows, appFilter, titleFilter, hostFilter]);

  // Client-side sort — all 11 columns sortable.
  const [sort, setSort] = useState<{ key: ColumnKey; direction: 'asc' | 'desc' }>({
    key: 'time',
    direction: 'desc',
  });

  const onSort = (key: ColumnKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : // Numeric columns default desc; text columns default asc.
          {
            key,
            direction: key === 'time' || key === 'events' || key === 'sessions' ? 'desc' : 'asc',
          },
    );
  };

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const d = sort.direction === 'asc' ? 1 : -1;
      switch (sort.key) {
        case 'employee':
          return d * (a.employeeName ?? '').localeCompare(b.employeeName ?? '');
        case 'app':
          return d * a.app.localeCompare(b.app);
        case 'title':
          return d * a.title.localeCompare(b.title);
        case 'host':
          return d * a.host.localeCompare(b.host);
        case 'client':
          return d * a.clientName.localeCompare(b.clientName);
        case 'project':
          return d * a.projectName.localeCompare(b.projectName);
        case 'start':
          return d * a.firstOccurredAt.localeCompare(b.firstOccurredAt);
        case 'end':
          return d * a.lastOccurredAt.localeCompare(b.lastOccurredAt);
        case 'sessions':
          return d * (a.sessions - b.sessions);
        case 'events':
          return d * (a.eventCount - b.eventCount);
        case 'time':
          return d * (a.totalHours - b.totalHours);
        default:
          return 0;
      }
    });
  }, [filteredRows, sort]);

  // Drill-down: clicking a row navigates to the per-row event list. The
  // URL carries the row identity (app + title) plus the backend filters
  // so the events page narrows to the same slice.
  const buildRowHref = (row: AppTitleLogRow): string => {
    const next = new URLSearchParams();
    next.set('app', row.app);
    next.set('title', row.title);
    if (row.clientId) next.set('clientId', row.clientId);
    if (row.projectId) next.set('projectId', row.projectId);
    if (row.employeeId) next.set('employeeId', row.employeeId);
    return `/reports/app-title-log/events?${next.toString()}`;
  };

  // Review: opens the events page in a new tab with review=1 so the AI
  // analysis panel fires automatically.
  const buildReviewHref = (row: AppTitleLogRow): string => buildRowHref(row) + '&review=1';

  const getRowKey = (row: AppTitleLogRow) =>
    [row.employeeId ?? '', row.app, row.title, row.host, row.clientId, row.projectId].join(
      ROW_KEY_SEP,
    );

  const columns: Array<{
    key: ColumnKey;
    header: string;
    sortable: boolean;
    align?: 'left' | 'right';
    render: (row: AppTitleLogRow) => React.ReactNode;
  }> = [
    {
      key: 'employee',
      header: 'Employee',
      sortable: true,
      render: (row) => <span className={styles.truncate}>{row.employeeName ?? '—'}</span>,
    },
    {
      key: 'app',
      header: 'App',
      sortable: true,
      render: (row) => (
        <span className={styles.truncate} title={row.app || undefined}>
          {row.app || '—'}
        </span>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (row) => (
        <span className={styles.truncate} title={row.title || undefined}>
          {row.title || '—'}
        </span>
      ),
    },
    {
      key: 'host',
      header: 'Host',
      sortable: true,
      render: (row) => (
        <span className={styles.truncate} title={row.host || undefined}>
          {row.host || '—'}
        </span>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      sortable: true,
      render: (row) => <span className={styles.truncate}>{row.clientName || '—'}</span>,
    },
    {
      key: 'project',
      header: 'Project',
      sortable: true,
      render: (row) => <span className={styles.truncate}>{row.projectName || '—'}</span>,
    },
    {
      key: 'start',
      header: 'Start',
      sortable: true,
      align: 'right',
      render: (row) => <span className={styles.mono}>{formatDayShort(row.firstOccurredAt)}</span>,
    },
    {
      key: 'end',
      header: 'End',
      sortable: true,
      align: 'right',
      render: (row) => <span className={styles.mono}>{formatDayShort(row.lastOccurredAt)}</span>,
    },
    {
      key: 'sessions',
      header: 'Sessions',
      sortable: true,
      align: 'right',
      render: (row) => <span className={styles.mono}>{row.sessions}</span>,
    },
    {
      key: 'events',
      header: 'Events',
      sortable: true,
      align: 'right',
      render: (row) => <span className={styles.mono}>{row.eventCount.toLocaleString()}</span>,
    },
    {
      key: 'time',
      header: 'Time',
      sortable: true,
      align: 'right',
      render: (row) => <span className={styles.mono}>{row.totalHours.toFixed(1)}h</span>,
    },
    {
      key: 'review',
      header: '',
      sortable: false,
      render: (row) => (
        <a
          href={buildReviewHref(row)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.reviewLink}
          onClick={(e) => e.stopPropagation()}
        >
          Review
        </a>
      ),
    },
  ];

  const summary = report?.summary;
  const footer =
    summary && sortedRows.length > 0 ? (
      <tr>
        <td colSpan={6} className={styles.footerLabel}>
          Grand Total
          {report && report.totalGroups > sortedRows.length
            ? ` (top ${sortedRows.length.toLocaleString()} of ${report.totalGroups.toLocaleString()})`
            : ''}
        </td>
        {/* start */}
        <td />
        {/* end */}
        <td />
        <td className={styles.mono}>{summary.totalSessions.toLocaleString()}</td>
        <td className={styles.mono}>{summary.totalEvents.toLocaleString()}</td>
        <td className={styles.mono}>{summary.totalHours.toFixed(1)}h</td>
      </tr>
    ) : null;

  return (
    <>
      <PageHeader
        title="Activity Log"
        subtitle="Activity grouped by employee, application, title, host, client, and project."
      />

      <div className={styles.reportControls}>
        <div className={styles.controlsRow}>
          <Select
            value={clientId || ''}
            options={[
              { value: '', label: 'All clients' },
              ...clients.map((c) => ({ value: c.id, label: c.name })),
            ]}
            onChange={onPickClient}
            label="Client"
            size="sm"
          />
          <Select
            value={projectId || ''}
            options={[
              { value: '', label: 'All projects' },
              ...filteredProjects.map((p) => ({ value: p.id, label: p.name })),
            ]}
            onChange={(v) => updateParam('projectId', v || null)}
            label="Project"
            size="sm"
          />
          <Select
            value={employeeId || ''}
            options={[
              { value: '', label: 'All employees' },
              ...employees.map((e) => ({ value: e.id, label: e.name })),
            ]}
            onChange={(v) => updateParam('employeeId', v || null)}
            label="Employee"
            size="sm"
          />
        </div>
        <div className={styles.controlsRow}>
          <TextInput
            value={appFilter}
            onChange={(e) => updateParam('filterApp', e.target.value || null)}
            label="App"
            placeholder="Filter by app…"
            size="sm"
          />
          <TextInput
            value={titleFilter}
            onChange={(e) => updateParam('filterTitle', e.target.value || null)}
            label="Title"
            placeholder="Filter by title…"
            size="sm"
          />
          <TextInput
            value={hostFilter}
            onChange={(e) => updateParam('filterHost', e.target.value || null)}
            label="Host"
            placeholder="Filter by host…"
            size="sm"
          />
        </div>
      </div>

      <DataTable<AppTitleLogRow, ColumnKey>
        caption="Activity grouped by employee, application, title, host, client, and project"
        columns={columns}
        rows={sortedRows}
        getRowKey={getRowKey}
        sort={sort}
        onSort={onSort}
        rowHref={buildRowHref}
        emptyState={isLoading ? 'Loading…' : 'No activity in this window.'}
        footer={footer}
      />
    </>
  );
}
